/**
 * Roles and what they may see.
 *
 * This module answers "should this be on screen". It never answers "is this
 * allowed" — that is settled in the API layer, every time, on every request.
 * Hiding a control is a courtesy to the user, not a security boundary.
 */

export const ROLES = ['staff', 'manager', 'owner'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  staff: 'Counter staff',
  manager: 'Manager',
  owner: 'Owner',
};

/**
 * Named capabilities rather than role checks scattered through components.
 *
 * Staff see money on the one booking in front of them, because they have to
 * type it in. They do not see totals. That split is deliberate: this is a
 * cash business.
 */
export const CAPABILITIES = [
  'bookings.record_payment',
  'bookings.cancel',
  'bookings.discount',
  'rates.edit',
  'reports.dues',
  'reports.revenue',
  'settings.manage',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

const CAPABILITIES_BY_ROLE: Readonly<Record<Role, readonly Capability[]>> = {
  staff: ['bookings.record_payment'],
  manager: [
    'bookings.record_payment',
    'bookings.cancel',
    'bookings.discount',
    'rates.edit',
    'reports.dues',
  ],
  owner: [...CAPABILITIES],
};

export function can(role: Role, capability: Capability): boolean {
  return CAPABILITIES_BY_ROLE[role].includes(capability);
}
